/**
 * HoActiveProjectDetail - Active in-progress project (Greenwood).
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 */

import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { useHomeownerProfile } from "@/features/settings/hooks/use-profile";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";
import { CommentsPanel, projectCommentSubjectAdapter } from "../../../comments";
import type { AvatarTone, HoProject } from "../../data/mock-homeowner";
import { useProjectBOQs } from "../../hooks/use-boq";
import { useProjectReviewEligibility } from "../../hooks/use-project-review-eligibility";
import {
	useProject,
	useProjectActivity,
	useProjectContractors,
	useProjectMilestones,
	useProjectPayments,
	useProjectProposals,
	useProjectShortlist,
} from "../../hooks/use-projects";
import {
	cancelProject,
	updateProject,
} from "../../services/projects-api.service";
import {
	DESIGNER_TAB_KEYS,
	HOMEOWNER_RETIRED_TAB_IDS,
	HOMEOWNER_TAB_IDS,
	HOMEOWNER_TABS as TABS,
	type HomeownerTabId as TabId,
} from "../../utils/project-tab-keys";
import {
	resolveTabKey,
	shouldRewriteTabKey,
} from "../../utils/resolve-tab-key";
import { ProjectReviewSection } from "../project-review-section";
import {
	Avatar,
	Eyebrow,
	formatINR as fmt,
	Icn,
	ItalicAccent,
	Progress,
	secondaryBtnStyle,
} from "./design-primitives";
import { HoFundingBanner } from "./ho-funding-banner";
import { HoVisibilityPanel } from "./ho-visibility-panel";
import {
	HoBoqTab,
	HoCostsTab,
	HoDocumentsTab,
	HoMessagesTab,
	HoMilestonesTab,
	HoOverviewTab,
	HoSelectionsTab,
	HoTeamTab,
} from "./tabs";

// The tab catalog of both surfaces lives in utils/project-tab-keys.ts; see the
// header there for why this one cannot own its own copy. Re-exported so the
// key lists stay importable from the surface they describe.
export { HOMEOWNER_RETIRED_TAB_IDS, HOMEOWNER_TAB_IDS };

interface HoActiveProjectDetailProps {
	project: HoProject;
	onBack: () => void;
}

export function HoActiveProjectDetail({
	project,
	onBack,
}: HoActiveProjectDetailProps) {
	const { t } = useTranslation();
	const accent = "var(--color-dashboard-accent)";
	const [searchParams, setSearchParams] = useSearchParams();
	const initialTab =
		resolveTabKey(
			searchParams.get("tab"),
			HOMEOWNER_RETIRED_TAB_IDS,
			HOMEOWNER_TAB_IDS,
		) ?? "overview";
	const [activeTab, setActiveTab] = React.useState<TabId>(initialTab);

	// Follow the URL when it changes under a mounted page -- a notification
	// opened from the header navigates in place, which changes only the query
	// string. The body moves first; rewriting the address bar without it is how
	// the URL and the page came to disagree.
	//
	// resolveTabKey is the whole resolution: it aliases, then membership-tests
	// what it aliased. Reading HOMEOWNER_RETIRED_TAB_IDS directly instead skips
	// that test and answers for keys nobody put in the map -- `?tab=constructor`
	// returns Object.prototype.constructor and writes a stringified function
	// into the address bar.
	React.useEffect(() => {
		const raw = searchParams.get("tab");
		const tab = resolveTabKey(
			raw,
			HOMEOWNER_RETIRED_TAB_IDS,
			HOMEOWNER_TAB_IDS,
		);
		if (!tab) return;
		setActiveTab(tab);
		if (shouldRewriteTabKey(raw, tab, DESIGNER_TAB_KEYS)) {
			// handleTabSwitch already writes the active tab to the URL on every
			// click, so this leaves a copied URL carrying the current key -- except
			// for a key the designer surface still has as a live tab, which is left
			// alone because only the raw key opens the right tab on both surfaces.
			// replace, not push, so Back leaves the page rather than returning to
			// the retired key.
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set("tab", tab);
					return next;
				},
				{ replace: true },
			);
		}
	}, [searchParams, setSearchParams]);
	// userId of the thread to auto-open when switching to the messages tab
	const [messageThreadUserId, setMessageThreadUserId] = React.useState<
		string | null
	>(searchParams.get("messageThreadUserId"));

	const { data: activities = [] } = useProjectActivity(project.id);
	const { data: milestones = [] } = useProjectMilestones(project.id);
	const { data: payments } = useProjectPayments(project.id);
	const { data: boqs } = useProjectBOQs(project.id);
	const { data: shortlist } = useProjectShortlist(project.id);
	const { data: projectData, refetch } = useProject(project.id);
	const reviewEligibility = useProjectReviewEligibility(
		project.id,
		projectData?.status === "completed",
	);
	const { data: contractorsData } = useProjectContractors(project.id);
	const { data: proposalItems, error: proposalsError } = useProjectProposals(
		project.id,
	);
	// The rule `getHomeownerProjects` counts by for the project card: a
	// submitted proposal on a request in proposal_submitted, the state in which
	// the Team tab offers Hire and Decline, on a project that is not cancelled
	// and has no designer hired. Nothing is counted until the project has
	// loaded, since either condition may then rule the proposals out.
	const proposalsCanAwaitDecision =
		!!projectData &&
		!projectData.designer &&
		projectData.status !== "cancelled";
	const proposalsAwaitingDecision = proposalsCanAwaitDecision
		? (proposalItems ?? []).filter(
				(item) =>
					item.request.status === "proposal_submitted" &&
					item.proposal?.status === "submitted",
			).length
		: 0;
	const proposalsUnavailable =
		proposalsCanAwaitDecision && !!proposalsError && !proposalItems;
	const contractors = contractorsData?.contractors;
	const { data: homeownerProfile } = useHomeownerProfile();

	// Project edit state
	const [isEditing, setIsEditing] = React.useState(false);
	const [editTitle, setEditTitle] = React.useState("");
	const [editScope, setEditScope] = React.useState<"full_home" | "partial">(
		"full_home",
	);
	const [editBudgetMin, setEditBudgetMin] = React.useState("");
	const [editBudgetMax, setEditBudgetMax] = React.useState("");
	const [editTimelineWeeks, setEditTimelineWeeks] = React.useState("");
	const [editBeforeImages, setEditBeforeImages] = React.useState<string[]>([]);
	const [isSaving, setIsSaving] = React.useState(false);
	const saveControllerRef = React.useRef<AbortController | null>(null);

	// Project cancel state
	const [isCancelling, setIsCancelling] = React.useState(false);
	const [cancelReason, setCancelReason] = React.useState("");
	const [isSubmittingCancel, setIsSubmittingCancel] = React.useState(false);

	const queryClient = useQueryClient();

	const handleFunded = React.useCallback(async (): Promise<void> => {
		await refetch();
		await queryClient.invalidateQueries({
			queryKey: queryKeys.projects.milestones(project.id),
		});
		await queryClient.invalidateQueries({
			queryKey: queryKeys.projects.payments(project.id),
		});
	}, [queryClient, refetch, project.id]);

	const isTerminal = projectData
		? projectData.status === "completed" || projectData.status === "cancelled"
		: project.stage === "Completed" || project.stage === "Cancelled";

	React.useEffect(() => {
		return () => {
			saveControllerRef.current?.abort();
		};
	}, []);

	const handleCloseEdit = React.useCallback(() => {
		saveControllerRef.current?.abort();
		setIsEditing(false);
	}, []);

	const startEditing = React.useCallback((): void => {
		if (!projectData) return;
		setEditTitle(projectData.title);
		setEditScope(projectData.scope);
		setEditBudgetMin(
			projectData.budgetMin != null ? String(projectData.budgetMin) : "",
		);
		setEditBudgetMax(
			projectData.budgetMax != null ? String(projectData.budgetMax) : "",
		);
		setEditTimelineWeeks(
			projectData.timelineWeeks != null
				? String(projectData.timelineWeeks)
				: "",
		);
		setEditBeforeImages(projectData.beforeImages || []);
		setIsEditing(true);
	}, [projectData]);

	const handleSaveEdit = React.useCallback(async (): Promise<void> => {
		if (!projectData || !editTitle.trim()) return;

		const bMin = editBudgetMin ? parseInt(editBudgetMin, 10) : undefined;
		const bMax = editBudgetMax ? parseInt(editBudgetMax, 10) : undefined;
		const tWeeks = editTimelineWeeks
			? parseInt(editTimelineWeeks, 10)
			: undefined;

		if (bMin !== undefined && (Number.isNaN(bMin) || bMin < 0)) {
			toast.error("Minimum budget must be a positive number");
			return;
		}
		if (bMax !== undefined && (Number.isNaN(bMax) || bMax < 0)) {
			toast.error("Maximum budget must be a positive number");
			return;
		}
		if (bMin !== undefined && bMax !== undefined && bMin > bMax) {
			toast.error("Minimum budget cannot be greater than maximum budget");
			return;
		}
		if (tWeeks !== undefined && (Number.isNaN(tWeeks) || tWeeks < 0)) {
			toast.error("Timeline must be a positive number");
			return;
		}

		saveControllerRef.current?.abort();
		const controller = new AbortController();
		saveControllerRef.current = controller;
		setIsSaving(true);
		try {
			const result = await updateProject(
				projectData.id,
				{
					title: editTitle.trim(),
					scope: editScope,
					budgetMin: bMin,
					budgetMax: bMax,
					timelineWeeks: tWeeks,
					beforeImages: editBeforeImages,
				},
				{ signal: controller.signal },
			);
			if (controller.signal.aborted) return;
			if (result.success) {
				await refetch();
				setIsEditing(false);
				toast.success("Project updated");
			} else {
				toast.error(result.error ?? "Failed to update project");
			}
		} catch (_err) {
			if (controller.signal.aborted) return;
			toast.error("Failed to update project");
		} finally {
			if (!controller.signal.aborted) {
				setIsSaving(false);
			}
		}
	}, [
		projectData,
		editTitle,
		editScope,
		editBudgetMin,
		editBudgetMax,
		editTimelineWeeks,
		editBeforeImages,
		refetch,
	]);

	const handleCancelProject = React.useCallback(async (): Promise<void> => {
		if (!projectData) return;

		setIsSubmittingCancel(true);
		try {
			const result = await cancelProject(
				projectData.id,
				cancelReason.trim() ? cancelReason.trim() : undefined,
			);
			if (result.success) {
				toast.success("Project cancelled successfully");
				setIsCancelling(false);
				setCancelReason("");
				await refetch();
			} else {
				toast.error(result.error ?? "Failed to cancel project");
			}
		} catch (_err) {
			toast.error("Failed to cancel project");
		} finally {
			setIsSubmittingCancel(false);
		}
	}, [projectData, cancelReason, refetch]);

	function handleMessageUser(userId: string): void {
		setMessageThreadUserId(userId);
		setActiveTab("messages");
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("tab", "messages");
			return next;
		});
	}

	function handleTabSwitch(id: TabId): void {
		// When switching away from messages via the tab bar, clear the pending
		// thread so navigating back to Messages directly doesn't auto-open it.
		if (id !== "messages") {
			setMessageThreadUserId(null);
		}
		setActiveTab(id);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("tab", id);
			return next;
		});
	}

	function getInitials(name: string): string {
		return name
			.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	}

	const teamMembers: Array<{
		name: string;
		ini: string;
		tone: "ink" | "cognac" | "terra" | "amber";
		role: string;
		type: "designer" | "contractor" | "homeowner";
	}> = [];

	if (projectData?.designer) {
		teamMembers.push({
			name: projectData.designer.name,
			ini: getInitials(projectData.designer.name),
			tone: "ink",
			role: "Lead Designer",
			type: "designer",
		});
	}

	contractors?.forEach((c, idx) => {
		if (c.status === "hired" || c.status === "completed") {
			teamMembers.push({
				name: c.contractor?.name || "Contractor",
				ini: getInitials(c.contractor?.name || "C"),
				tone: ["cognac", "terra", "amber"][idx % 3] as
					| "cognac"
					| "terra"
					| "amber",
				role: c.contractor?.trades?.join(", ") || "Execution Partner",
				type: "contractor",
			});
		}
	});

	if (homeownerProfile) {
		teamMembers.push({
			name: homeownerProfile.name || "You",
			ini: getInitials(homeownerProfile.name || "Y"),
			tone: "terra",
			role: "Homeowner",
			type: "homeowner",
		});
	}

	const totalMilestones = milestones.length;
	const completedMilestones = milestones.filter(
		(m) => m.status === "completed",
	).length;
	const progressPct =
		totalMilestones > 0
			? Math.round((completedMilestones / totalMilestones) * 100)
			: 0;

	const inEscrow = payments?.summary.totalHeld ?? project.escrow;
	const spent = payments?.summary.totalReleased ?? 0;

	// The milestones an open funding order covers, from the `pending` rows it
	// wrote. `fundProject` resumes one only for a request that names exactly its
	// milestones and refuses every other set, so these are what the banner offers
	// to reopen — and a project with one open is funded enough to need the banner
	// even when its own `funding_status` has not caught up.
	const inFlightMilestoneIds = React.useMemo(
		() =>
			Array.from(
				new Set(
					(payments?.transactions ?? [])
						.filter((txn) => txn.status === "pending" && txn.milestoneId)
						.map((txn) => txn.milestoneId as string),
				),
			),
		[payments?.transactions],
	);

	const createdAt = new Date(project.posted ? project.posted : new Date());
	const timelineWeeks = project.days / 7 || 12;
	const handoverDate = new Date(
		createdAt.getTime() + timelineWeeks * 7 * 24 * 60 * 60 * 1000,
	);
	const daysUntilHandover = Math.ceil(
		(handoverDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
	);

	const summary = project.summary || project.brief || "";

	const summaryPills = {
		inExecution: project.pct > 0 && project.pct < 100,
		pendingApprovals: milestones.filter(
			(m) => m.status === "pending" || m.paymentStatus === "not_paid",
		).length,
	};

	const stats = {
		progress: progressPct,
		inEscrow,
		spent,
		budget: project.budget,
		daysLeft: daysUntilHandover > 0 ? daysUntilHandover : 0,
	};

	const pendingMilestones = milestones.filter(
		(m) => m.status !== "completed",
	).length;
	// `!selectedQuoteId`, and note this also fixes a latent bug: the old check was
	// `!s.selectedQuoteIndex`, which is falsy for index 0 -- so an entry where the
	// homeowner had approved the FIRST quote counted as still pending.
	const pendingSelections =
		shortlist?.filter((s) => !s.selectedQuoteId).length ?? 0;
	const pendingBoqCount =
		boqs?.filter((boq) => boq.status === "sent").length ?? 0;
	// A count of rows the homeowner can act on, not of money: a row HoCostsTab
	// renders a button on. That is the same test as its `fundableMilestoneId`
	// and `releasableMilestoneId`: a `pending` row gets Fund escrow and a `held`
	// row gets Release, each only while the row names a milestone, because both
	// calls are addressed by milestone. So three kinds of row are not counted: a
	// contractor assignment, which `hireContractor` writes at `held` with no
	// milestone; a top-up, which `topUpEscrow` writes with none, and which the
	// Team tab's Add funds acts on rather than this tab; and a row whose
	// milestone was deleted. It is deliberately not a funded total
	// -- summing the two statuses would present an unpaid row as money in
	// escrow, which is the lie this term must not repeat.
	const actionablePayments =
		payments?.transactions?.filter(
			(t) => (t.status === "held" || t.status === "pending") && t.milestoneId,
		).length ?? 0;
	// One badge for one tab, and this is the tab with the buttons. Costs &
	// Payments carried this count before BOQ absorbed it, and HoCostsTab moved
	// into the BOQ body with it, so both terms count items the reader acts on
	// without leaving this tab.
	//
	// That this badge and the funding notification name different tabs is
	// deliberate, not an oversight. `project_awaiting_funding` carries
	// ?tab=payments, which HOMEOWNER_RETIRED_TAB_IDS sends to Milestones, and it
	// is a different moment in the lifecycle: fundProject is what inserts escrow
	// rows, so at awaiting_funding there are none and this term is 0. Rows exist
	// only after funding, and the action on one is the button in the BOQ body.
	// Nothing routes a notification at a held or pending transaction. See
	// docs/plans/2026-09-24-0700-refactor-project-detail-tabs-plan.md for the
	// alternative that was rejected.
	const pendingBoqBadge = pendingBoqCount + actionablePayments;

	return (
		<div style={{ paddingTop: 0 }}>
			{/* ========== MASTHEAD ========== */}
			<section
				className="px-4 md:px-12"
				style={{
					maxWidth: 1240,
					margin: "0 auto",
					paddingTop: 36,
					paddingBottom: 20,
				}}
			>
				<button
					type="button"
					onClick={onBack}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 7,
						border: 0,
						background: "transparent",
						cursor: "pointer",
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						fontWeight: 600,
						color: "var(--color-dashboard-muted)",
						marginBottom: 18,
						padding: 0,
					}}
					onMouseEnter={(e) =>
						((e.currentTarget as HTMLElement).style.color =
							"var(--color-dashboard-accent-dark)")
					}
					onMouseLeave={(e) =>
						((e.currentTarget as HTMLElement).style.color =
							"var(--color-dashboard-muted)")
					}
				>
					<Icn name="arrowLeft" size={16} />
					My projects
				</button>

				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 16,
						marginTop: -28,
					}}
				>
					<div style={{ flex: 1 }} />

					<div style={{ display: "flex", gap: 10 }}>
						{!isTerminal && (
							<button
								type="button"
								onClick={() => setIsCancelling(true)}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 5,
									padding: "5px 12px",
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 6,
									background:
										"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 60%, var(--color-dashboard-divider) 100%)",
									boxShadow:
										"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent), inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 60%, transparent), inset 0 0 0 1px color-mix(in srgb, var(--color-border-default) 70%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
									cursor: "pointer",
									fontFamily: "var(--font-body)",
									fontSize: 12,
									fontWeight: 600,
									color: "var(--color-dashboard-danger)",
									letterSpacing: "0.02em",
								}}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLElement).style.color =
										"var(--color-dashboard-pill-risk)";
									(e.currentTarget as HTMLElement).style.borderColor =
										"var(--color-dashboard-pill-risk)";
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLElement).style.color =
										"var(--color-dashboard-danger)";
									(e.currentTarget as HTMLElement).style.borderColor =
										"var(--color-dashboard-border)";
								}}
							>
								<Icn name="x" size={12} color="currentColor" />
								Cancel Project
							</button>
						)}

						<button
							type="button"
							onClick={startEditing}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 5,
								padding: "5px 12px",
								border: "1px solid var(--color-border-default)",
								borderRadius: 6,
								background:
									"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 60%, var(--color-dashboard-divider) 100%)",
								boxShadow:
									"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent), inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 60%, transparent), inset 0 0 0 1px color-mix(in srgb, var(--color-border-default) 70%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
								cursor: "pointer",
								fontFamily: "var(--font-body)",
								fontSize: 12,
								fontWeight: 600,
								color: "var(--color-dashboard-muted-dark)",
								letterSpacing: "0.02em",
							}}
							onMouseEnter={(e) => {
								(e.currentTarget as HTMLElement).style.color =
									"var(--color-dashboard-accent-dark)";
								(e.currentTarget as HTMLElement).style.borderColor =
									"var(--color-dashboard-accent-light)";
							}}
							onMouseLeave={(e) => {
								(e.currentTarget as HTMLElement).style.color =
									"var(--color-dashboard-muted-dark)";
								(e.currentTarget as HTMLElement).style.borderColor =
									"var(--color-border-default)";
							}}
						>
							<Icn
								name="pen"
								size={12}
								color="var(--color-dashboard-muted-dark)"
							/>
							Edit
						</button>
					</div>
				</div>

				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-end",
						gap: 32,
						flexWrap: "wrap",
					}}
				>
					<div style={{ flex: 1, minWidth: 0 }}>
						<Eyebrow style={{ fontSize: 10, letterSpacing: "0.22em" }}>
							DAY {project.day} OF {project.days} · {project.loc.toUpperCase()}
						</Eyebrow>
						<h1
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 48,
								lineHeight: 1.04,
								letterSpacing: "-0.025em",
								color: "var(--color-dashboard-text)",
								margin: "12px 0 0",
							}}
						>
							{(() => {
								const parts = project.name.split(" ");
								const last = parts.pop() ?? "";
								return (
									<>
										{parts.join(" ")}
										{parts.length > 0 ? " " : ""}
										<ItalicAccent size={48} color={accent}>
											{last}
										</ItalicAccent>
									</>
								);
							})()}
						</h1>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 10,
								marginTop: 14,
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted-dark)",
								flexWrap: "wrap",
							}}
						>
							<span>{project.config}</span>
							<span style={{ color: "var(--color-dashboard-accent-dark)" }}>
								·
							</span>
							<ItalicAccent size={15} color="var(--color-dashboard-muted-dark)">
								{project.style}
							</ItalicAccent>
							<span style={{ color: "var(--color-dashboard-accent-dark)" }}>
								·
							</span>
							<span
								style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
							>
								<Avatar
									initials={project.designerIni ?? ""}
									tone={(project.designerTone as AvatarTone) ?? "amber"}
									size={22}
								/>
								{project.designer} · {project.studio}
							</span>
						</div>
					</div>

					<div
						style={{
							display: "flex",
							gap: 18,
							alignItems: "flex-end",
							flexWrap: "wrap",
						}}
					>
						<div
							style={{
								textAlign: "right",
								paddingRight: 20,
								borderRight: "1px solid var(--color-border-default)",
							}}
						>
							<Eyebrow style={{ fontSize: 9, letterSpacing: "0.18em" }}>
								RELEASED FROM ESCROW
							</Eyebrow>
							<div
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 24,
									color: "var(--color-dashboard-text)",
									marginTop: 4,
									letterSpacing: "-0.015em",
								}}
							>
								{fmt(spent)}
								<span
									style={{
										fontFamily: "var(--font-body)",
										fontStyle: "italic",
										fontSize: 13,
										color: "var(--color-dashboard-muted)",
									}}
								>
									{" "}
									/ {fmt(project.budget)}
								</span>
							</div>
							<Progress
								value={Math.round((spent / project.budget) * 100)}
								fill={accent}
								style={{ width: 190, marginTop: 8 }}
							/>
						</div>
						<StudioButton
							variant="secondary"
							size="md"
							onClick={() => {
								if (projectData?.designer?.userId) {
									handleMessageUser(projectData.designer.userId);
								}
							}}
							style={secondaryBtnStyle("md")}
						>
							<Icn name="message" size={16} />
							Message {project.designer?.split(" ")[0] ?? "Team"}
						</StudioButton>
					</div>
				</div>
			</section>

			{/* ========== FUNDING BANNER ========== */}
			<HoFundingBanner
				projectId={project.id}
				milestones={milestones}
				inFlightMilestoneIds={inFlightMilestoneIds}
				visible={
					projectData?.status === "awaiting_funding" ||
					projectData?.fundingStatus === "partially_funded" ||
					inFlightMilestoneIds.length > 0
				}
				onFunded={handleFunded}
			/>

			{/* ========== EDIT PROJECT FORM OVERLAY ========== */}
			<EditProjectOverlay
				isOpen={isEditing}
				onClose={handleCloseEdit}
				title={editTitle}
				setTitle={setEditTitle}
				scope={editScope}
				setScope={setEditScope}
				budgetMin={editBudgetMin}
				setBudgetMin={setEditBudgetMin}
				budgetMax={editBudgetMax}
				setBudgetMax={setEditBudgetMax}
				timelineWeeks={editTimelineWeeks}
				setTimelineWeeks={setEditTimelineWeeks}
				beforeImages={editBeforeImages}
				setBeforeImages={setEditBeforeImages}
				onSave={handleSaveEdit}
				isSaving={isSaving}
			/>

			{/* ========== CANCEL PROJECT FORM OVERLAY ========== */}
			<CancelProjectOverlay
				isOpen={isCancelling}
				onClose={() => {
					setIsCancelling(false);
					setCancelReason("");
				}}
				reason={cancelReason}
				setReason={setCancelReason}
				onConfirm={handleCancelProject}
				isSubmitting={isSubmittingCancel}
			/>

			{/* ========== STICKY TAB BAR ========== */}
			<div
				style={{
					position: "sticky",
					top: "var(--app-header-h, 0px)",
					zIndex: 100,
					background:
						"color-mix(in srgb, var(--color-dashboard-surface) 96%, transparent)",
					borderBottom: "1px solid var(--color-dashboard-border)",
					borderTop: "1px solid var(--color-dashboard-border)",
				}}
			>
				<div
					className="px-4 md:px-12"
					style={{
						maxWidth: 1240,
						margin: "0 auto",
						display: "flex",
						gap: 26,
						overflowX: "auto",
					}}
				>
					{TABS.map(({ id, labelKey, icon }) => {
						const on = activeTab === id;
						const badge =
							id === "boq"
								? pendingBoqBadge
								: id === "milestones"
									? pendingMilestones
									: id === "selections"
										? pendingSelections
										: 0;
						return (
							<button
								key={id}
								type="button"
								onClick={() => handleTabSwitch(id)}
								style={{
									position: "relative",
									display: "flex",
									alignItems: "center",
									gap: 8,
									padding: "16px 4px",
									border: 0,
									background: "transparent",
									cursor: "pointer",
									fontFamily: on ? "var(--font-display)" : "var(--font-body)",
									fontStyle: on ? "italic" : "normal",
									fontSize: on ? 17 : 13.5,
									fontWeight: 500,
									whiteSpace: "nowrap",
									color: on
										? "var(--color-dashboard-text)"
										: "var(--color-dashboard-muted)",
								}}
							>
								<Icn
									name={icon}
									size={16}
									color={on ? accent : "var(--color-dashboard-placeholder)"}
								/>
								{t(labelKey)}
								{badge != null && badge > 0 && (
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											minWidth: 17,
											height: 17,
											padding: "0 4px",
											borderRadius: 6,
											background: accent,
											color: "var(--color-dashboard-on-fill)",
											fontFamily: "var(--font-body)",
											fontStyle: "normal",
											fontWeight: 600,
											fontSize: 11,
										}}
									>
										{badge}
									</span>
								)}
								{on && (
									<span
										style={{
											position: "absolute",
											left: 0,
											right: 0,
											bottom: -1,
											height: 2,
											background: accent,
											borderRadius: 1,
										}}
									/>
								)}
							</button>
						);
					})}
				</div>
			</div>

			{/* ========== TAB CONTENT ========== */}
			<div
				key={activeTab}
				className="px-4 md:px-12"
				style={{
					animation: "fadeIn 320ms ease",
					maxWidth: 1240,
					margin: "0 auto",
					paddingTop: 36,
					paddingBottom: 70,
				}}
			>
				{activeTab === "overview" && (
					<HoOverviewTab
						project={project}
						activities={activities}
						stats={stats}
						summary={summary}
						summaryPills={summaryPills}
						accent={accent}
						// handleTabSwitch, not setActiveTab. HoOverviewTab calls this with
						// "team" and "messages", which are tab switches like any the tab
						// bar makes: setActiveTab alone moves the body and leaves the URL
						// on the tab the reader left, and skips the pending-thread clear
						// handleTabSwitch does when the target is not messages.
						onNav={handleTabSwitch}
						teamMembers={teamMembers}
						proposalsAwaitingDecision={proposalsAwaitingDecision}
						proposalsUnavailable={proposalsUnavailable}
						visibilitySlot={
							projectData ? (
								<HoVisibilityPanel
									projectId={project.id}
									status={projectData.status}
									isPublic={projectData.isPublic ?? false}
									onRefetch={refetch}
								/>
							) : null
						}
						reviewSlot={
							projectData?.status === "completed" ? (
								reviewEligibility.isError ? (
									<p className="font-body text-sm text-dashboard-muted-dark">
										{t("projectReview.loadFailed")}{" "}
										<button
											type="button"
											className="cursor-pointer underline"
											onClick={() => void reviewEligibility.refetch()}
										>
											{t("projectReview.retry")}
										</button>
									</p>
								) : (
									<ProjectReviewSection
										projectId={project.id}
										eligibility={reviewEligibility.data ?? null}
										isLoading={reviewEligibility.isLoading}
										onSaved={async () => {
											await reviewEligibility.refetch();
										}}
									/>
								)
							) : undefined
						}
					/>
				)}
				{activeTab === "team" && (
					<HoTeamTab projectId={project.id} onMessageUser={handleMessageUser} />
				)}
				{activeTab === "messages" && (
					<div style={{ display: "grid", gap: 40 }}>
						<HoMessagesTab
							projectId={project.id}
							defaultThreadUserId={messageThreadUserId}
						/>
						<CommentsPanel
							subjectId={project.id}
							adapter={projectCommentSubjectAdapter}
						/>
					</div>
				)}
				{activeTab === "boq" && (
					<div style={{ display: "grid", gap: 40 }}>
						<HoCostsTab
							projectId={project.id}
							accent={accent}
							designerStudio={project.studio ?? undefined}
						/>
						<HoBoqTab
							projectId={project.id}
							accent={accent}
							onAskDesigner={handleMessageUser}
						/>
					</div>
				)}
				{activeTab === "milestones" && (
					<HoMilestonesTab
						projectId={project.id}
						accent={accent}
						designerName={project.designer || undefined}
						designerStudio={project.studio || undefined}
						onAskDesigner={handleMessageUser}
					/>
				)}
				{activeTab === "selections" && (
					<HoSelectionsTab projectId={project.id} accent={accent} />
				)}
				{activeTab === "documents" && <HoDocumentsTab projectId={project.id} />}
			</div>
		</div>
	);
}

interface EditProjectOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	setTitle: (val: string) => void;
	scope: "full_home" | "partial";
	setScope: (val: "full_home" | "partial") => void;
	budgetMin: string;
	setBudgetMin: (val: string) => void;
	budgetMax: string;
	setBudgetMax: (val: string) => void;
	timelineWeeks: string;
	setTimelineWeeks: (val: string) => void;
	beforeImages: string[];
	setBeforeImages: (val: string[]) => void;
	onSave: () => Promise<void>;
	isSaving: boolean;
}

function EditProjectOverlay({
	isOpen,
	onClose,
	title,
	setTitle,
	scope,
	setScope,
	budgetMin,
	setBudgetMin,
	budgetMax,
	setBudgetMax,
	timelineWeeks,
	setTimelineWeeks,
	beforeImages,
	setBeforeImages,
	onSave,
	isSaving,
}: EditProjectOverlayProps) {
	const overlayRef = React.useRef<HTMLDivElement>(null);
	const beforeImagesRef = React.useRef<string[]>(beforeImages);
	React.useEffect(() => {
		beforeImagesRef.current = beforeImages;
	}, [beforeImages]);

	React.useEffect(() => {
		if (!isOpen) return;

		// Focus the first interactive element or container
		const focusableElements = overlayRef.current?.querySelectorAll<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		if (focusableElements && focusableElements.length > 0) {
			focusableElements[0].focus();
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key === "Tab") {
				if (!overlayRef.current) return;
				const elements = Array.from(
					overlayRef.current.querySelectorAll<HTMLElement>(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					),
				);
				if (elements.length === 0) return;

				const firstElement = elements[0];
				const lastElement = elements[elements.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === firstElement) {
						lastElement.focus();
						e.preventDefault();
					}
				} else {
					if (document.activeElement === lastElement) {
						firstElement.focus();
						e.preventDefault();
					}
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;
	return (
		<>
			<button
				type="button"
				style={{
					position: "fixed",
					inset: 0,
					background: "rgba(30,41,59,0.45)",
					zIndex: 200,
					backdropFilter: "blur(4px)",
					border: "none",
					cursor: "pointer",
					width: "100vw",
					height: "100vh",
					padding: 0,
				}}
				onClick={onClose}
				tabIndex={-1}
				aria-label="Close overlay"
			/>
			<div
				ref={overlayRef}
				style={{
					position: "fixed",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					zIndex: 201,
					width: "100%",
					maxWidth: 520,
					maxHeight: "90vh",
					overflowY: "auto",
				}}
			>
				<div
					style={{
						margin: "0 24px",
						borderRadius: 14,
						border: "1px solid var(--color-border-default)",
						background:
							"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 60%, var(--color-dashboard-divider) 100%)",
						boxShadow:
							"0 24px 64px -16px color-mix(in srgb, var(--color-dashboard-shadow-ink) 28%, transparent), 0 8px 24px -8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 90%, transparent), inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 40%, transparent)",
						overflow: "hidden",
					}}
				>
					<div
						style={{
							padding: "28px 32px 20px",
							borderBottom: "1px solid var(--color-dashboard-border)",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<div>
								<Eyebrow
									style={{
										fontSize: 9,
										letterSpacing: "0.22em",
										color: "var(--color-dashboard-placeholder)",
									}}
								>
									EDIT PROJECT DETAILS
								</Eyebrow>
								<h2
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 26,
										fontStyle: "italic",
										color: "var(--color-dashboard-text)",
										marginTop: 6,
										letterSpacing: "-0.02em",
									}}
								>
									Update your project
								</h2>
							</div>
							<button
								type="button"
								onClick={onClose}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 36,
									height: 36,
									borderRadius: 7,
									border: "1px solid var(--color-border-default)",
									background: "transparent",
									cursor: "pointer",
									color: "var(--color-dashboard-muted)",
								}}
							>
								<Icn name="x" size={16} />
							</button>
						</div>
					</div>
					<div
						style={{
							padding: "24px 32px 32px",
							display: "flex",
							flexDirection: "column",
							gap: 20,
						}}
					>
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<label
								htmlFor="project-title"
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									fontWeight: 700,
									letterSpacing: "0.18em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								Project Title
							</label>
							<input
								id="project-title"
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								style={{
									width: "100%",
									height: 44,
									padding: "0 14px",
									borderRadius: 7,
									border: "1px solid var(--color-border-default)",
									background: "var(--color-dashboard-paper)",
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-text)",
									outline: "none",
									boxSizing: "border-box",
									boxShadow:
										"inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
								}}
								onFocus={(e) =>
									(e.target.style.borderColor = "var(--color-dashboard-accent)")
								}
								onBlur={(e) =>
									(e.target.style.borderColor = "var(--color-border-default)")
								}
								placeholder="e.g. Greenwood Apartment"
							/>
						</div>
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								id="scope-label"
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									fontWeight: 700,
									letterSpacing: "0.18em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								Scope
							</span>
							<div
								role="radiogroup"
								aria-labelledby="scope-label"
								style={{ display: "flex", gap: 10 }}
							>
								{(["full_home", "partial"] as const).map((sc) => {
									const radioSpan = (
										// biome-ignore lint/a11y/useSemanticElements: custom styled radio button
										<span
											key={sc}
											role="radio"
											tabIndex={0}
											aria-checked={scope === sc}
											onClick={() => setScope(sc)}
											onKeyDown={(e) => {
												if (e.key === " " || e.key === "Enter") {
													setScope(sc);
													e.preventDefault();
												}
											}}
											style={{
												flex: 1,
												height: 44,
												borderRadius: 7,
												border:
													scope === sc
														? "1px solid var(--color-dashboard-accent)"
														: "1px solid var(--color-border-default)",
												background:
													scope === sc
														? "linear-gradient(180deg, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, color-mix(in srgb, var(--color-dashboard-accent) 4%, transparent) 100%)"
														: "var(--color-dashboard-paper)",
												cursor: "pointer",
												fontFamily: "var(--font-body)",
												fontSize: 13,
												fontWeight: 600,
												color:
													scope === sc
														? "var(--color-dashboard-accent-dark)"
														: "var(--color-dashboard-muted-dark)",
												boxShadow:
													scope === sc
														? "inset 0 0 0 1px color-mix(in srgb, var(--color-dashboard-accent) 20%, transparent)"
														: "none",
												transition: "all 200ms ease",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											{sc === "full_home" ? "Full home" : "Partial"}
										</span>
									);
									return radioSpan;
								})}
							</div>
						</div>
						<div style={{ display: "flex", gap: 12 }}>
							<div
								style={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									gap: 6,
								}}
							>
								<label
									htmlFor="budget-min"
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9,
										fontWeight: 700,
										letterSpacing: "0.18em",
										color: "var(--color-dashboard-muted)",
										textTransform: "uppercase",
									}}
								>
									Min Budget (Rs)
								</label>
								<input
									id="budget-min"
									type="number"
									value={budgetMin}
									onChange={(e) => setBudgetMin(e.target.value)}
									style={{
										width: "100%",
										height: 44,
										padding: "0 14px",
										borderRadius: 7,
										border: "1px solid var(--color-border-default)",
										background: "var(--color-dashboard-paper)",
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										outline: "none",
										boxSizing: "border-box",
										boxShadow:
											"inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
									}}
									onFocus={(e) =>
										(e.target.style.borderColor =
											"var(--color-dashboard-accent)")
									}
									onBlur={(e) =>
										(e.target.style.borderColor = "var(--color-border-default)")
									}
									placeholder="e.g. 2000000"
								/>
							</div>
							<div
								style={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									gap: 6,
								}}
							>
								<label
									htmlFor="budget-max"
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9,
										fontWeight: 700,
										letterSpacing: "0.18em",
										color: "var(--color-dashboard-muted)",
										textTransform: "uppercase",
									}}
								>
									Max Budget (Rs)
								</label>
								<input
									id="budget-max"
									type="number"
									value={budgetMax}
									onChange={(e) => setBudgetMax(e.target.value)}
									style={{
										width: "100%",
										height: 44,
										padding: "0 14px",
										borderRadius: 7,
										border: "1px solid var(--color-border-default)",
										background: "var(--color-dashboard-paper)",
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										outline: "none",
										boxSizing: "border-box",
										boxShadow:
											"inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
									}}
									onFocus={(e) =>
										(e.target.style.borderColor =
											"var(--color-dashboard-accent)")
									}
									onBlur={(e) =>
										(e.target.style.borderColor = "var(--color-border-default)")
									}
									placeholder="e.g. 3000000"
								/>
							</div>
						</div>
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<label
								htmlFor="timeline-weeks"
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									fontWeight: 700,
									letterSpacing: "0.18em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								Timeline (weeks)
							</label>
							<input
								id="timeline-weeks"
								type="number"
								value={timelineWeeks}
								onChange={(e) => setTimelineWeeks(e.target.value)}
								style={{
									width: "100%",
									height: 44,
									padding: "0 14px",
									borderRadius: 7,
									border: "1px solid var(--color-border-default)",
									background: "var(--color-dashboard-paper)",
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-text)",
									outline: "none",
									boxSizing: "border-box",
									boxShadow:
										"inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
								}}
								onFocus={(e) =>
									(e.target.style.borderColor = "var(--color-dashboard-accent)")
								}
								onBlur={(e) =>
									(e.target.style.borderColor = "var(--color-border-default)")
								}
								placeholder="e.g. 12"
							/>
						</div>
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									fontWeight: 700,
									letterSpacing: "0.18em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								Before Images
							</span>
							<MultiImageUpload
								label="Before images"
								currentUrls={beforeImages}
								maxImages={24}
								onUpload={(urls) =>
									setBeforeImages([...beforeImagesRef.current, ...urls])
								}
								onRemove={(index) =>
									setBeforeImages(
										beforeImagesRef.current.filter((_, i) => i !== index),
									)
								}
							/>
						</div>
						<div style={{ display: "flex", gap: 10, marginTop: 4 }}>
							<button
								type="button"
								onClick={onClose}
								disabled={isSaving}
								style={{
									flex: 1,
									height: 46,
									borderRadius: 8,
									border: "1px solid var(--color-border-default)",
									background:
										"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 60%, var(--color-dashboard-divider) 100%)",
									boxShadow:
										"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent), inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 60%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
									cursor: isSaving ? "not-allowed" : "pointer",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
									fontWeight: 600,
									color: "var(--color-dashboard-muted-dark)",
									opacity: isSaving ? 0.6 : 1,
								}}
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={onSave}
								disabled={isSaving || !title.trim()}
								style={{
									flex: 1,
									height: 46,
									borderRadius: 8,
									border: "none",
									background:
										isSaving || !title.trim()
											? "var(--color-dashboard-warm-300)"
											: "var(--color-dashboard-accent)",
									boxShadow:
										"inset 0 1px 0 rgba(255,237,213,0.35), inset 0 -1px 0 color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 18%, transparent), 0 6px 18px -6px color-mix(in srgb, var(--color-dashboard-accent) 35%, transparent)",
									cursor: isSaving || !title.trim() ? "not-allowed" : "pointer",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
									fontWeight: 600,
									color: "var(--color-dashboard-on-fill)",
								}}
							>
								{isSaving ? "Saving..." : "Save changes"}
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

interface CancelProjectOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	reason: string;
	setReason: (val: string) => void;
	onConfirm: () => Promise<void>;
	isSubmitting: boolean;
}

function CancelProjectOverlay({
	isOpen,
	onClose,
	reason,
	setReason,
	onConfirm,
	isSubmitting,
}: CancelProjectOverlayProps) {
	const overlayRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (!isOpen) return;

		const focusableElements = overlayRef.current?.querySelectorAll<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		if (focusableElements && focusableElements.length > 0) {
			focusableElements[0].focus();
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key === "Tab") {
				if (!overlayRef.current) return;
				const elements = Array.from(
					overlayRef.current.querySelectorAll<HTMLElement>(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					),
				);
				if (elements.length === 0) return;

				const firstElement = elements[0];
				const lastElement = elements[elements.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === firstElement) {
						lastElement.focus();
						e.preventDefault();
					}
				} else {
					if (document.activeElement === lastElement) {
						firstElement.focus();
						e.preventDefault();
					}
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;
	return (
		<>
			<button
				type="button"
				style={{
					position: "fixed",
					inset: 0,
					background: "rgba(30,41,59,0.45)",
					zIndex: 200,
					backdropFilter: "blur(4px)",
					border: "none",
					cursor: "pointer",
					width: "100vw",
					height: "100vh",
					padding: 0,
				}}
				onClick={onClose}
				tabIndex={-1}
				aria-label="Close overlay"
			/>
			<div
				ref={overlayRef}
				style={{
					position: "fixed",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					zIndex: 201,
					width: "100%",
					maxWidth: 520,
					maxHeight: "90vh",
					overflowY: "auto",
				}}
			>
				<div
					style={{
						margin: "0 24px",
						borderRadius: 14,
						border: "1px solid var(--color-border-default)",
						background:
							"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 60%, var(--color-dashboard-divider) 100%)",
						boxShadow:
							"0 24px 64px -16px color-mix(in srgb, var(--color-dashboard-shadow-ink) 28%, transparent), 0 8px 24px -8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 90%, transparent), inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 40%, transparent)",
						overflow: "hidden",
					}}
				>
					<div
						style={{
							padding: "28px 32px 20px",
							borderBottom: "1px solid var(--color-dashboard-border)",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<div>
								<Eyebrow
									style={{
										fontSize: 9,
										letterSpacing: "0.22em",
										color: "var(--color-dashboard-danger)",
									}}
								>
									CANCEL PROJECT
								</Eyebrow>
								<h2
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 26,
										fontStyle: "italic",
										color: "var(--color-dashboard-text)",
										marginTop: 6,
										letterSpacing: "-0.02em",
									}}
								>
									Are you sure?
								</h2>
							</div>
							<button
								type="button"
								onClick={onClose}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 36,
									height: 36,
									borderRadius: 7,
									border: "1px solid var(--color-border-default)",
									background: "transparent",
									cursor: "pointer",
									color: "var(--color-dashboard-muted)",
								}}
							>
								<Icn name="x" size={16} />
							</button>
						</div>
					</div>
					<div
						style={{
							padding: "24px 32px 32px",
							display: "flex",
							flexDirection: "column",
							gap: 20,
						}}
					>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14.5,
								lineHeight: 1.6,
								color: "var(--color-dashboard-muted-dark)",
								margin: 0,
							}}
						>
							Cancelling this project is a permanent action. All active
							collaborations, pending designer proposals, and milestones will be
							halted.
						</p>
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<label
								htmlFor="cancel-reason"
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									fontWeight: 700,
									letterSpacing: "0.18em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								Reason for cancellation (optional)
							</label>
							<textarea
								id="cancel-reason"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								rows={3}
								style={{
									width: "100%",
									padding: "12px 14px",
									borderRadius: 7,
									border: "1px solid var(--color-border-default)",
									background: "var(--color-dashboard-paper)",
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-text)",
									outline: "none",
									boxSizing: "border-box",
									boxShadow:
										"inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
									resize: "none",
								}}
								onFocus={(e) =>
									(e.target.style.borderColor = "var(--color-dashboard-accent)")
								}
								onBlur={(e) =>
									(e.target.style.borderColor = "var(--color-border-default)")
								}
								placeholder="Let us know why you are cancelling..."
							/>
						</div>
						<div style={{ display: "flex", gap: 10, marginTop: 4 }}>
							<button
								type="button"
								onClick={onClose}
								disabled={isSubmitting}
								style={{
									flex: 1,
									height: 46,
									borderRadius: 8,
									border: "1px solid var(--color-border-default)",
									background:
										"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-paper) 60%, var(--color-dashboard-divider) 100%)",
									boxShadow:
										"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent), inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 60%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
									cursor: isSubmitting ? "not-allowed" : "pointer",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
									fontWeight: 600,
									color: "var(--color-dashboard-muted-dark)",
									opacity: isSubmitting ? 0.6 : 1,
								}}
							>
								Keep Project
							</button>
							<button
								type="button"
								onClick={onConfirm}
								disabled={isSubmitting}
								style={{
									flex: 1,
									height: 46,
									borderRadius: 8,
									border: "none",
									background: isSubmitting
										? "var(--color-dashboard-warm-300)"
										: "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 45%, rgba(0,0,0,0.06) 100%), linear-gradient(180deg, var(--color-dashboard-danger-fill) 0%, #B91C1C 60%, #991B1B 100%)",
									boxShadow:
										"inset 0 1px 0 rgba(255,237,213,0.35), inset 0 -1px 0 color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 18%, transparent)",
									cursor: isSubmitting ? "not-allowed" : "pointer",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
									fontWeight: 600,
									color: "var(--color-dashboard-on-fill)",
								}}
							>
								{isSubmitting ? "Cancelling..." : "Cancel Project"}
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
